import { useEffect, useState } from "react";
import api from '../../services/apiClient.ts';
import { toast } from "react-toastify";
import Modal from "../common/Modal";
import { useTranslation } from "react-i18next";
import { Loader, Search as SearchIcon, Plus, Pencil, Trash2 } from "lucide-react";

export default function TravelersPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [travelers, setTravelers] = useState([]);

  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    name: "",
    phone1: "",
    phone2: "",
    passport: "",
  });

  const headersAuth = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "X-Tenant": localStorage.getItem("tenant_key"),
    "X-Branch": localStorage.getItem("branch_id"),
  };

  const resetForm = () => {
    setForm({
      name: "",
      phone1: "",
      phone2: "",
      passport: "",
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await api.get("/v1/travelers", {
        headers: headersAuth,
        params: {
          q: search || undefined,
          per_page: 50,
        },
      });
      setTravelers(resp.data.data || []);
    } catch (err) {
      console.log(err);
      toast.error(t("travelers.loadError"));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return toast.error(t("travelers.validationRequired"));
    if (!form.passport.trim()) return toast.error(t("travelers.validationRequired"));

    try {
      await api.post(
        "/v1/travelers",
        {
          name: form.name,
          phone1: form.phone1 || null,
          phone2: form.phone2 || null,
          passport: form.passport,
        },
        { headers: headersAuth }
      );

      toast.success(t("travelers.saveSuccess"));
      resetForm();
      setOpenAdd(false);
      loadData();
    } catch (err) {
      console.log(err);
      if (err.response?.data?.errors) {
        const errs = err.response.data.errors;
        const firstKey = Object.keys(errs)[0];
        toast.error(errs[firstKey][0]);
      } else {
        toast.error(t("travelers.saveError"));
      }
    }
  };

  const handleEdit = async () => {
    if (!selected) return;

    if (!form.name.trim()) return toast.error(t("travelers.validationRequired"));
    if (!form.passport.trim()) return toast.error(t("travelers.validationRequired"));

    try {
      await api.put(
        `/v1/travelers/${selected.id}`,
        {
          name: form.name,
          phone1: form.phone1 || null,
          phone2: form.phone2 || null,
          passport: form.passport,
        },
        { headers: headersAuth }
      );

      toast.success(t("travelers.saveSuccess"));
      resetForm();
      setSelected(null);
      setOpenEdit(false);
      loadData();
    } catch (err) {
      console.log(err);
      if (err.response?.data?.errors) {
        const errs = err.response.data.errors;
        const firstKey = Object.keys(errs)[0];
        toast.error(errs[firstKey][0]);
      } else {
        toast.error(t("travelers.saveError"));
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("travelers.deleteConfirmation"))) return;

    try {
      await api.delete(`/v1/travelers/${id}`, {
        headers: headersAuth,
      });
      toast.success(t("travelers.deleteSuccess"));
      loadData();
    } catch (err) {
      console.log(err);
      toast.error(t("travelers.deleteError"));
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    loadData();
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
          <Loader className="h-6 w-6 animate-spin" />
          <span className="text-lg font-semibold">{t("loading")}</span>
        </div>
      </div>
    );

  return (
    <div className="w-full mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6 rtl:flex-row-reverse">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
            {t("travelers.title")}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("travelers.subtitle")}</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setOpenAdd(true);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 shadow-sm"
        >
          <Plus className="h-4 w-4" />
          {t("travelers.addTraveler")}
        </button>
      </div>

      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex items-center gap-2 rtl:flex-row-reverse">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 rtl:right-3 rtl:left-auto top-2.5 h-4 w-4 text-gray-400" />
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-9 py-2 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={t("travelers.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary-600 hover:bg-primary-700 text-white px-4 py-2"
          >
            {t("common.search")}
          </button>
        </form>
      </div>

      <div className="app-table-wrap shadow-sm">
        <table className="app-table min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">#</th>
              <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t("travelers.fullName")}</th>
              <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t("travelers.primaryPhone")}</th>
              <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t("travelers.secondaryPhone")}</th>
              <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t("travelers.passportNumber")}</th>
              <th className="px-4 py-3 text-right rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {travelers.map((tItem, i) => (
              <tr key={tItem.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-700 dark:text-gray-200">{i + 1}</td>
                <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-900 dark:text-gray-100">{tItem.name}</td>
                <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-700 dark:text-gray-200">{tItem.phone1 || "-"}</td>
                <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-700 dark:text-gray-200">{tItem.phone2 || "-"}</td>
                <td className="px-4 py-3 text-right rtl:text-right text-sm text-gray-700 dark:text-gray-200">{tItem.passport}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 rtl:flex-row-reverse text-right justify-start">
                    <button
                      className="inline-flex items-center gap-1 rounded-md bg-primary-500 hover:bg-primary-600 text-white px-3 py-1.5"
                      onClick={() => {
                        setSelected(tItem);
                        setForm({
                          name: tItem.name || "",
                          phone1: tItem.phone1 || "",
                          phone2: tItem.phone2 || "",
                          passport: tItem.passport || "",
                        });
                        setOpenEdit(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                      {t("common.edit")}
                    </button>
                    <button
                      className="inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white px-3 py-1.5"
                      onClick={() => handleDelete(tItem.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("common.delete")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {travelers.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t("travelers.noTravelers")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal show={openAdd} onClose={() => setOpenAdd(false)} title={t("travelers.addTraveler")}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.fullName")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t("travelers.fullNamePlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.primaryPhone")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.phone1}
              onChange={(e) => setForm({ ...form, phone1: e.target.value })}
              placeholder={t("travelers.primaryPhonePlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.secondaryPhone")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.phone2}
              onChange={(e) => setForm({ ...form, phone2: e.target.value })}
              placeholder={t("travelers.secondaryPhonePlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.passportNumber")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.passport}
              onChange={(e) => setForm({ ...form, passport: e.target.value })}
              placeholder={t("travelers.passportNumberPlaceholder")}
            />
          </div>
          <div className="flex items-center justify-end gap-2 rtl:flex-row-reverse pt-2">
            <button
              className="rounded-md bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-200 px-4 py-2 border border-gray-200 dark:border-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-700"
              onClick={() => setOpenAdd(false)}
            >
              {t("common.cancel")}
            </button>
            <button
              className="rounded-md bg-primary-600 hover:bg-primary-700 text-white px-4 py-2"
              onClick={handleAdd}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal show={openEdit} onClose={() => setOpenEdit(false)} title={t("travelers.editTraveler")}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.fullName")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t("travelers.fullNamePlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.primaryPhone")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.phone1}
              onChange={(e) => setForm({ ...form, phone1: e.target.value })}
              placeholder={t("travelers.primaryPhonePlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.secondaryPhone")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.phone2}
              onChange={(e) => setForm({ ...form, phone2: e.target.value })}
              placeholder={t("travelers.secondaryPhonePlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">{t("travelers.passportNumber")}</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.passport}
              onChange={(e) => setForm({ ...form, passport: e.target.value })}
              placeholder={t("travelers.passportNumberPlaceholder")}
            />
          </div>
          <div className="flex items-center justify-end gap-2 rtl:flex-row-reverse pt-2">
            <button
              className="rounded-md bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-200 px-4 py-2 border border-gray-200 dark:border-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-700"
              onClick={() => setOpenEdit(false)}
            >
              {t("common.cancel")}
            </button>
            <button
              className="rounded-md bg-primary-600 hover:bg-primary-700 text-white px-4 py-2"
              onClick={handleEdit}
            >
              {t("common.update")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
